import { useState } from "react";
import type { DebtReason, PublicGameState, RoomMember } from "@grundbesitz/shared";
import { formatMoney } from "../../../lib/format";
import type { DebtView, RaiseOption } from "../../../lib/assets";
import { Button } from "../../ui/Button";
import { PlayerChip } from "./PlayerChip";

const REASON: Record<DebtReason, string> = {
  RENT: "Miete",
  TAX: "Steuer",
  CARD: "Kartenzahlung",
  BIRTHDAY: "Geburtstagsgeld",
  JAIL_FINE: "Gefängniskaution",
  MORTGAGE_INTEREST: "Hypothekenzinsen",
  REPAIRS: "Renovierung",
};

/**
 * Debt mode header: what is missing, what is the most that can be raised, and the ways to raise it
 * ordered cheapest first. Nothing runs by itself, every row is one deliberate click.
 */
export function DebtBanner({
  game,
  members,
  debt,
  options,
  busy,
  onRun,
  onBankrupt,
}: {
  game: PublicGameState;
  members: RoomMember[];
  debt: DebtView;
  options: RaiseOption[];
  busy: string | null;
  onRun: (o: RaiseOption) => void;
  onBankrupt: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const needed = options.filter((o) => o.needed);
  const neededGain = needed.reduce((sum, o) => sum + o.gain, 0);

  return (
    <section
      aria-label="Schuldenmodus"
      className="rounded-xl border-2 border-danger bg-surface p-3.5 shadow-sm"
    >
      <h3 className="font-display text-lg font-semibold leading-tight">Du musst zahlen, bevor es weitergeht</h3>
      <ul className="mt-1.5 space-y-0.5 text-sm">
        {debt.debts.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center gap-x-1.5">
            <span className="tabular-nums font-semibold">{formatMoney(d.amount)}</span>
            <span className="text-ink-2">{REASON[d.reason]} an</span>
            {d.creditor === "bank" ? (
              <span className="font-semibold">die Bank</span>
            ) : (
              <PlayerChip game={game} members={members} id={d.creditor} size={16} />
            )}
          </li>
        ))}
      </ul>

      <dl className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-surface-2 px-2 py-2">
          <dt className="text-xs text-ink-2">Bargeld</dt>
          <dd className="tabular-nums text-sm font-semibold">{formatMoney(debt.cash)}</dd>
        </div>
        <div className="rounded-lg border border-danger bg-surface px-2 py-2" aria-live="polite">
          <dt className="text-xs text-ink-2">Es fehlen</dt>
          <dd className="tabular-nums text-lg font-bold text-danger">{formatMoney(debt.shortfall)}</dd>
        </div>
        <div className="rounded-lg bg-surface-2 px-2 py-2">
          <dt className="text-xs text-ink-2">Maximal erzielbar</dt>
          <dd className="tabular-nums text-sm font-semibold">{formatMoney(debt.raisable)}</dd>
        </div>
      </dl>
      <p className="mt-1.5 text-xs text-ink-2">
        Maximal erzielbar zählt Bargeld, den Verkauf aller Gebäude und alle möglichen Hypotheken zusammen.
        Bauen und Hypotheken auflösen sind gesperrt, bis die Schuld bezahlt ist.
      </p>

      {debt.hopeless ? (
        <div className="mt-3 rounded-lg border border-danger/60 p-3 text-sm">
          <p>
            <strong>Auch mit allem, was du verkaufen kannst, reicht es nicht.</strong> Du kannst Bankrott erklären.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {confirming ? (
              <>
                <Button variant="danger" onClick={onBankrupt} disabled={busy !== null}>
                  Ja, Bankrott erklären
                </Button>
                <Button variant="ghost" onClick={() => setConfirming(false)}>
                  Abbrechen
                </Button>
              </>
            ) : (
              <Button variant="secondary" onClick={() => setConfirming(true)}>
                Bankrott erklären
              </Button>
            )}
          </div>
        </div>
      ) : (
        options.length > 0 && (
          <div className="mt-3">
            <h4 className="text-sm font-semibold">Vorschläge, billigste zuerst</h4>
            <p className="text-xs text-ink-2">
              {needed.length > 0
                ? `Die ersten ${needed.length} decken den Fehlbetrag (+${formatMoney(neededGain)}). Du entscheidest, was du davon tust.`
                : "Der Fehlbetrag ist gedeckt."}
            </p>
            <ol className="mt-2 space-y-1.5">
              {options.map((o, i) => (
                <li
                  key={`${o.kind}-${o.field}`}
                  className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${
                    o.needed ? "border-accent bg-accent-soft" : "border-line bg-surface"
                  }`}
                >
                  <span aria-hidden="true" className="w-5 text-center text-xs font-bold tabular-nums text-ink-2">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1 text-sm">
                    <span className="block font-semibold">{o.label}</span>
                    <span className="text-xs text-ink-2">
                      +{formatMoney(o.gain)}
                      {o.needed ? ", nötig" : ""}
                    </span>
                  </span>
                  <Button
                    variant={o.needed ? "primary" : "secondary"}
                    className="shrink-0"
                    disabled={busy !== null}
                    aria-label={`${o.label}, +${formatMoney(o.gain)}`}
                    onClick={() => onRun(o)}
                  >
                    {o.kind === "MORTGAGE" ? "Belasten" : "Verkaufen"}
                  </Button>
                </li>
              ))}
            </ol>
          </div>
        )
      )}
    </section>
  );
}
